import { useCallback, useEffect, useRef } from 'react';

import { CUSTOMER_DATA_TABS, LEAD_DETAILS_FORMS } from 'cpool-constants';
import type { SchemaLoanRequestResolver_Get } from 'cpool-tables/backend-resolvers';
import { useForm } from 'react-hook-form';

import { useLeadDetailsFormContext } from '~/features/leadDetails/context/LeadDetailsFormContext.tsx';
import { useTabErrorNotification } from '~/features/leadDetails/hooks/useTabErrorNotification.ts';
import type { SchemaExpensesForm } from '~/features/leadDetails/schemas/expenses.schema.ts';
import { resolverExpensesForm } from '~/features/leadDetails/schemas/expenses.schema.ts';
import { generateExpenseCommands } from '~/features/leadDetails/utils/generateExpenseCommands.ts';
import { submitCommands } from '~/features/leadDetails/utils/submitCommands.ts';
import { toExpensesFormValues } from '~/features/leadDetails/utils/toExpensesFormValues.ts';
import { useOnMountAndChange } from '~/hooks/useOnMountAndChange.tsx';

const formId = LEAD_DETAILS_FORMS.EXPENSES;

export const useExpensesForm = (lead: SchemaLoanRequestResolver_Get) => {
    const { registerDirtyState, registerReset, registerSubmit } = useLeadDetailsFormContext();
    const methods = useForm<SchemaExpensesForm>({
        defaultValues: toExpensesFormValues(lead.haushalt.ausgaben),
        mode: 'onChange',
        resolver: resolverExpensesForm,
    });
    const hadAntragsteller2 = useRef(!!lead.antragsteller2);

    useEffect(() => {
        if (!hadAntragsteller2.current && lead.antragsteller2) {
            hadAntragsteller2.current = true;
            methods.reset(toExpensesFormValues(lead.haushalt.ausgaben));
        }
    }, [lead, lead.antragsteller2, methods]);

    useTabErrorNotification(
        CUSTOMER_DATA_TABS.INCOME_AND_EXPENSES,
        methods.formState.errors,
        LEAD_DETAILS_FORMS.EXPENSES,
    );

    useEffect(() => {
        registerDirtyState(formId, {
            dirty: methods.formState.isDirty,
            dirtyFields: methods.formState.dirtyFields,
        });
    }, [registerDirtyState, methods.formState, methods.formState.isDirty]);

    registerReset(formId, methods.reset);

    useOnMountAndChange(() => {
        registerSubmit(
            (setSubmitStatus) =>
                methods.handleSubmit(
                    async (data) => {
                        const commands = generateExpenseCommands(lead.haushalt.ausgaben, data);
                        await submitCommands(lead.id, setSubmitStatus, commands, methods.reset);
                    },
                    (error) => setSubmitStatus({ changes: true, error, saved: false }),
                ),
            formId,
            methods.trigger,
        );
    }, [lead.haushalt.ausgaben, lead.id, methods, registerSubmit]);

    const onDelete = useCallback(
        (type: keyof SchemaExpensesForm, id: string) => {
            const expenses = methods.getValues(type);
            methods.setValue(
                type,
                (expenses ?? []).filter((expense) => expense.id !== id),
                { shouldDirty: true },
            );
        },
        [methods],
    );

    return { methods, onDelete };
};

export type ExpensesFormState = ReturnType<typeof useExpensesForm>;
